import { useState, useMemo, useRef, useEffect, useId } from 'react'
import { ChevronsUpDown, Search, Check } from 'lucide-react'
import { Popover, PopoverContent, PopoverTrigger } from './popover'
import { Input } from './input'
import { Button } from './button'
import { cn } from '@/lib/utils'
import {
  SDR_BC_COUNTRIES,
  SDR_BC_REGION_ORDER,
  SDR_BC_COUNTRY_BY_ISO,
  countryFlagEmoji,
  normalizeForSearch,
  type SdrBcCountry,
  type SdrBcRegion,
} from '@/lib/sdr-bc-countries'

interface CountryComboboxProps {
  /** Selected ISO-2 code, or '' when nothing is picked. */
  value: string
  onChange: (iso: string) => void
  /** ISO codes already chosen in sibling rows — shown but not selectable (dedupe). */
  disabledIsos?: string[]
  disabled?: boolean
  placeholder?: string
  className?: string
  id?: string
}

// Single-select, searchable country picker scoped to the SDR-BC-supported
// markets. Type "me" → Mexico, "turkiye" → Türkiye (accent-insensitive), or the
// ISO itself ("br"). Grouped by region; keyboard arrows + Enter to pick.
export function CountryCombobox({
  value,
  onChange,
  disabledIsos = [],
  disabled = false,
  placeholder = 'Search country…',
  className,
  id,
}: CountryComboboxProps) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [highlight, setHighlight] = useState(0)
  const searchRef = useRef<HTMLInputElement>(null)
  const listRef = useRef<HTMLDivElement>(null)
  const listId = useId()
  const optionId = (iso: string) => `${listId}-opt-${iso}`

  const disabledSet = useMemo(() => new Set(disabledIsos.map(s => s.toUpperCase())), [disabledIsos])
  const selected = value ? SDR_BC_COUNTRY_BY_ISO[value.toUpperCase()] ?? null : null

  // Filter (accent-insensitive) by name, ISO, or region label.
  const filtered = useMemo<SdrBcCountry[]>(() => {
    const q = normalizeForSearch(query)
    if (!q) return SDR_BC_COUNTRIES
    return SDR_BC_COUNTRIES.filter(
      c =>
        normalizeForSearch(c.name).includes(q) ||
        c.iso.toLowerCase().startsWith(q) ||
        normalizeForSearch(c.region).includes(q),
    )
  }, [query])

  // Group the filtered list by region for display, preserving region order.
  const groups = useMemo<Array<{ region: SdrBcRegion; countries: SdrBcCountry[] }>>(() => {
    return SDR_BC_REGION_ORDER.map(region => ({
      region,
      countries: filtered.filter(c => c.region === region),
    })).filter(g => g.countries.length > 0)
  }, [filtered])

  // Flat list of navigable (non-disabled) options in display order — the
  // keyboard highlight indexes into this.
  const navigable = useMemo(
    () => groups.flatMap(g => g.countries).filter(c => !disabledSet.has(c.iso)),
    [groups, disabledSet],
  )

  // Focus the search box when the popover opens (DOM side-effect only — the
  // query/highlight reset happens in onOpenChange so we don't setState here).
  useEffect(() => {
    if (!open) return
    // Radix mounts content async; defer focus a tick.
    const t = setTimeout(() => searchRef.current?.focus(), 0)
    return () => clearTimeout(t)
  }, [open])

  // Keep the highlighted row scrolled into view.
  useEffect(() => {
    if (!open) return
    const el = listRef.current?.querySelector<HTMLElement>('[data-highlighted="true"]')
    el?.scrollIntoView({ block: 'nearest' })
  }, [highlight, open])

  function pick(iso: string) {
    onChange(iso)
    setOpen(false)
    setQuery('')
  }

  function onSearchKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setHighlight(h => Math.min(h + 1, Math.max(navigable.length - 1, 0)))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setHighlight(h => Math.max(h - 1, 0))
    } else if (e.key === 'Enter') {
      // Never let Enter bubble to the surrounding <form>.
      e.preventDefault()
      const choice = navigable[highlight]
      if (choice) pick(choice.iso)
    } else if (e.key === 'Escape') {
      e.preventDefault()
      setOpen(false)
    }
  }

  const highlightedIso = navigable[highlight]?.iso

  return (
    <Popover
      open={open}
      onOpenChange={o => {
        if (disabled) return
        setOpen(o)
        if (o) {
          setQuery('')
          setHighlight(0)
        }
      }}
    >
      <PopoverTrigger asChild>
        <Button
          id={id}
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn('w-full justify-between font-normal h-9 text-sm px-3', className)}
        >
          {selected ? (
            <span className="flex items-center gap-2 min-w-0">
              <span aria-hidden="true">{countryFlagEmoji(selected.iso)}</span>
              <span className="truncate">{selected.name}</span>
              <span className="text-xs text-muted-foreground shrink-0">{selected.region}</span>
            </span>
          ) : value ? (
            // Legacy / unknown ISO that isn't in the supported catalog — show it
            // raw so an edit-mode prefill never loses the AE's data.
            <span className="flex items-center gap-2 min-w-0">
              <span aria-hidden="true">{countryFlagEmoji(value)}</span>
              <span className="truncate font-mono uppercase">{value}</span>
            </span>
          ) : (
            <span className="truncate text-muted-foreground">{placeholder}</span>
          )}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" aria-hidden="true" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[var(--radix-popover-trigger-width)] min-w-[15rem] p-0"
        align="start"
        onOpenAutoFocus={e => e.preventDefault()}
      >
        <div className="p-2 border-b">
          <div className="relative">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
            <Input
              ref={searchRef}
              value={query}
              onChange={e => { setQuery(e.target.value); setHighlight(0) }}
              onKeyDown={onSearchKeyDown}
              placeholder="Type a country or ISO (e.g. me → Mexico)"
              className="pl-7 h-8 text-sm"
              role="combobox"
              aria-expanded={open}
              aria-controls={listId}
              aria-autocomplete="list"
              aria-activedescendant={highlightedIso ? optionId(highlightedIso) : undefined}
            />
          </div>
        </div>
        {/* sr-only live region: announce result count / empty state to AT. */}
        <div className="sr-only" role="status" aria-live="polite">
          {groups.length === 0 ? 'No matching country' : `${navigable.length} countries available`}
        </div>
        <div
          ref={listRef}
          id={listId}
          role="listbox"
          aria-label="Countries"
          className="max-h-64 overflow-y-auto p-1 scroll-pt-8"
        >
          {groups.length === 0 && (
            <p className="text-center text-sm text-muted-foreground py-6">No matching country</p>
          )}
          {groups.map(group => (
            <div key={group.region} role="group" aria-label={group.region}>
              <div className="px-2 py-1.5 text-xs font-medium text-muted-foreground sticky top-0 bg-popover">
                {group.region}
              </div>
              {group.countries.map(country => {
                const isDisabled = disabledSet.has(country.iso)
                const isSelected = selected?.iso === country.iso
                const isHighlighted = highlightedIso === country.iso
                return (
                  <button
                    key={country.iso}
                    id={optionId(country.iso)}
                    type="button"
                    role="option"
                    aria-selected={isSelected}
                    aria-disabled={isDisabled || undefined}
                    disabled={isDisabled}
                    data-highlighted={isHighlighted ? 'true' : undefined}
                    onClick={() => pick(country.iso)}
                    onMouseMove={() => {
                      // mousemove (not mouseenter) so scroll-induced enter events
                      // during keyboard nav don't steal the highlight.
                      const idx = navigable.findIndex(c => c.iso === country.iso)
                      if (idx >= 0 && idx !== highlight) setHighlight(idx)
                    }}
                    className={cn(
                      'flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm text-left transition-colors',
                      isDisabled
                        ? 'opacity-40 cursor-not-allowed'
                        : 'hover:bg-muted cursor-pointer',
                      isHighlighted && !isDisabled && 'bg-muted',
                    )}
                  >
                    <span aria-hidden="true" className="w-5 text-center">{countryFlagEmoji(country.iso)}</span>
                    <span className="flex-1 truncate">{country.name}</span>
                    <span className="text-xs text-muted-foreground font-mono">{country.iso}</span>
                    {isDisabled ? (
                      <span className="text-[10px] text-muted-foreground">added</span>
                    ) : isSelected ? (
                      <Check className="h-3.5 w-3.5 text-primary" aria-hidden="true" />
                    ) : null}
                  </button>
                )
              })}
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  )
}
